// src/services/api.js
const API_URL = process.env.REACT_APP_API_URL || 'https://rentia-app.onrender.com/api';

// Helper para manejar errores
const handleResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || 'Error en la petición');
  }
  return data;
};

// Helper: decodifica el payload del JWT (sin verificar firma) y chequea expiración
function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp && Date.now() / 1000 > payload.exp;
  } catch {
    return true; // token malformado = tratar como expirado
  }
}

// Helper para obtener headers con token
const getHeaders = (includeAuth = false) => {
  const headers = { 'Content-Type': 'application/json' };
  if (includeAuth) {
    const token = localStorage.getItem('token');
    if (token) {
      if (isTokenExpired(token)) {
        // Token expirado: limpiar sesión y recargar para ir al login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.reload();
        return headers;
      }
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  return headers;
};

/* ===========================
 *  Auth Service
 * =========================== */
export const authService = {
  // Login
  login: async (email, password) => {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ email, password }),
    });
    const data = await handleResponse(response);

    // Guardar token y user en localStorage
    if (data.token) {
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    return data;
  },

  // Logout
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  // Usuario actual (desde localStorage)
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // ¿Autenticado?
  isAuthenticated: () => !!localStorage.getItem('token'),

  // Registro público
  register: async (nombre, email, password) => {
    const response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ nombre, email, password }),
    });
    return handleResponse(response);
  },

  // Solicitar recuperación de contraseña
  forgotPassword: async (email) => {
    const response = await fetch(`${API_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ email }),
    });
    return handleResponse(response);
  },

  // Resetear contraseña con token
  resetPassword: async (token, newPassword) => {
    const response = await fetch(`${API_URL}/auth/reset-password`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ token, newPassword }),
    });
    return handleResponse(response);
  },

  // Verificar email con token
  verifyEmail: async (token) => {
    const response = await fetch(`${API_URL}/auth/verify-email?token=${token}`, {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse(response);
  },

  // Perfil
  getProfile: async () => {
    const response = await fetch(`${API_URL}/auth/profile`, {
      method: 'GET',
      headers: getHeaders(true),
    });
    return handleResponse(response);
  },

  // Cambiar contraseña
  changePassword: async (oldPassword, newPassword) => {
    const response = await fetch(`${API_URL}/auth/change-password`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify({ oldPassword, newPassword }),
    });
    return handleResponse(response);
  },
};

/* ===========================
 *  Users Service (ADMIN)
 * =========================== */
export const userService = {
  createUser: async (userData) => {
    const response = await fetch(`${API_URL}/users`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  getAllUsers: async () => {
    const response = await fetch(`${API_URL}/users`, {
      method: 'GET',
      headers: getHeaders(true),
    });
    return handleResponse(response);
  },

  resetPassword: async (userId, newPassword) => {
    const response = await fetch(`${API_URL}/users/${userId}/reset-password`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify({ newPassword }),
    });
    return handleResponse(response);
  },

  toggleUserActive: async (userId, activo) => {
    const response = await fetch(`${API_URL}/users/${userId}/toggle-active`, {
      method: 'PUT',
      headers: getHeaders(true),
      body: JSON.stringify({ activo }),
    });
    return handleResponse(response);
  },

deleteUser: async (userId) => {
    const response = await fetch(`${API_URL}/users/${userId}`, {
      method: 'DELETE',
      headers: getHeaders(true),
    });
    return handleResponse(response);
  },

  setExpiration: async (userId, fecha_vencimiento) => {
    const response = await fetch(`${API_URL}/users/${userId}/set-expiration`, {
      method: 'PUT',
      headers: getHeaders(true),
      body: JSON.stringify({ fecha_vencimiento }),
    });
    return handleResponse(response);
  },

  deleteAllContracts: async (userId) => {
    const response = await fetch(`${API_URL}/users/${userId}/contracts`, {
      method: 'DELETE',
      headers: getHeaders(true),
    });
    return handleResponse(response);
  },

  // Get email configuration
  getEmailConfig: async () => {
    const response = await fetch(`${API_URL}/users/email-config`, {
      method: 'GET',
      headers: getHeaders(true),
    });
    return handleResponse(response);
  },

  // Update email configuration
  updateEmailConfig: async (emailConfig) => {
    const response = await fetch(`${API_URL}/users/email-config`, {
      method: 'PUT',
      headers: getHeaders(true),
      body: JSON.stringify(emailConfig),
    });
    return handleResponse(response);
  },
};

/* ===========================
 *  Contracts Service
 *  /api/contratos
 * =========================== */
export const contractsService = {
  getAll: async () => {
    const res = await fetch(`${API_URL}/contratos`, {
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, contracts: [...] }
  },

  getDeleted: async () => {
    const res = await fetch(`${API_URL}/contratos/deleted`, {
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, contracts: [...] }
  },

  create: async (payload) => {
    const res = await fetch(`${API_URL}/contratos`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify(payload),
    });
    return handleResponse(res); // { success, message, contractId }
  },

  update: async (id, payload) => {
    const res = await fetch(`${API_URL}/contratos/${id}`, {
      method: 'PUT',
      headers: getHeaders(true),
      body: JSON.stringify(payload),
    });
    return handleResponse(res); // { success, message }
  },

  softDelete: async (id) => {
    const res = await fetch(`${API_URL}/contratos/${id}`, {
      method: 'DELETE',
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, message }
  },

  restore: async (id) => {
    const res = await fetch(`${API_URL}/contratos/${id}/restore`, {
      method: 'POST',
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, message }
  },

  duplicate: async (id) => {
    const res = await fetch(`${API_URL}/contratos/${id}/duplicate`, {
      method: 'POST',
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, message, contractId }
  },

  permanentDelete: async (id) => {
    const res = await fetch(`${API_URL}/contratos/${id}/permanent`, {
      method: 'DELETE',
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, message }
  },
};

/* ===========================
 *  Payments Service
 *  /api/pagos
 * =========================== */
export const paymentsService = {
  // Obtener todos los pagos
  getAll: async () => {
    const res = await fetch(`${API_URL}/pagos`, {
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, payments: [...] }
  },

  // Obtener pagos por contrato
  getByContract: async (contractId) => {
    const res = await fetch(`${API_URL}/pagos/contrato/${contractId}`, {
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, payments: [...] }
  },

  // Crear nuevo pago
  create: async (payload) => {
    const res = await fetch(`${API_URL}/pagos`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify(payload),
    });
    return handleResponse(res); // { success, message, paymentId }
  },

  // Registrar pago manual
  registerManual: async (payload) => {
    const res = await fetch(`${API_URL}/pagos/register-manual`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify(payload),
    });
    return handleResponse(res); // { success, message, paymentId }
  },

  // Actualizar pago
  update: async (id, payload) => {
    const res = await fetch(`${API_URL}/pagos/${id}`, {
      method: 'PUT',
      headers: getHeaders(true),
      body: JSON.stringify(payload),
    });
    return handleResponse(res); // { success, message }
  },

  // Eliminar pago
  delete: async (id) => {
    const res = await fetch(`${API_URL}/pagos/${id}`, {
      method: 'DELETE',
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, message }
  },

  // Marcar como pagado
  markAsPaid: async (id, payload) => {
    const res = await fetch(`${API_URL}/pagos/${id}/mark-paid`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify(payload),
    });
    return handleResponse(res); // { success, message }
  },

  // Actualizar estado del pago  // 👈 NUEVO MÉTODO
  updateStatus: async (id, status) => {
    const res = await fetch(`${API_URL}/pagos/${id}/status`, {
      method: 'PUT',
      headers: getHeaders(true),
      body: JSON.stringify({ status }),
    });
    return handleResponse(res);
  },
};

/* ===========================
 *  Receipts Service
 *  /api/receipts
 * =========================== */
export const receiptsService = {
  // Upload receipt photo
  uploadPhoto: async (paymentId, photoFile) => {
    const formData = new FormData();
    formData.append('photo', photoFile);

    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/receipts/${paymentId}/upload-photo`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData,
    });
    return handleResponse(res); // { success, message, photoUrl }
  },

  // Generate PDF receipt
  generatePDF: async (paymentId) => {
    const res = await fetch(`${API_URL}/receipts/${paymentId}/generate-pdf`, {
      method: 'POST',
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, message, pdfUrl }
  },

  // Download receipt (PDF or photo)
  downloadReceipt: async (paymentId, type = 'pdf') => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/receipts/${paymentId}/download?type=${type}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      },
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || 'Error al descargar el recibo');
    }

    // Return blob for download
    const blob = await res.blob();
    return blob;
  },

  // Delete receipt photo
  deletePhoto: async (paymentId) => {
    const res = await fetch(`${API_URL}/receipts/${paymentId}/photo`, {
      method: 'DELETE',
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, message }
  },

  // Get receipt URL for display
  getPhotoUrl: (photoPath) => {
    if (!photoPath) return null;
    // If already a full URL, return as is
    if (photoPath.startsWith('http')) return photoPath;
    // Otherwise, construct full URL
    return `${API_URL.replace('/api', '')}${photoPath}`;
  },

  getPdfUrl: (pdfPath) => {
    if (!pdfPath) return null;
    // If already a full URL, return as is
    if (pdfPath.startsWith('http')) return pdfPath;
    // Otherwise, construct full URL
    return `${API_URL.replace('/api', '')}${pdfPath}`;
  },
};

/* ===========================
 *  Incidents Service
 *  /api/incidencias
 * =========================== */
export const incidentsService = {
  // Obtener todas las incidencias
  getAll: async () => {
    const res = await fetch(`${API_URL}/incidencias`, {
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, incidents: [...] }
  },

  // Obtener incidencias por contrato
  getByContract: async (contractId) => {
    const res = await fetch(`${API_URL}/incidencias/contrato/${contractId}`, {
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, incidents: [...] }
  },

  // Crear incidencia
  create: async (payload) => {
    const res = await fetch(`${API_URL}/incidencias`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify(payload),
    });
    return handleResponse(res); // { success, message, incidentId }
  },

  // Actualizar incidencia
  update: async (id, payload) => {
    const res = await fetch(`${API_URL}/incidencias/${id}`, {
      method: 'PUT',
      headers: getHeaders(true),
      body: JSON.stringify(payload),
    });
    return handleResponse(res); // { success, message }
  },

  // Eliminar incidencia
  delete: async (id) => {
    const res = await fetch(`${API_URL}/incidencias/${id}`, {
      method: 'DELETE',
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, message }
  },

  // Marcar como resuelta
  markAsResolved: async (id) => {
    const res = await fetch(`${API_URL}/incidencias/${id}/resolver`, {
      method: 'POST',
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, message }
  },
};

/* ===========================
 *  Increases Service
 *  /api/aumentos
 * =========================== */
export const increasesService = {
  // Obtener aumentos por contrato
  getByContract: async (contractId) => {
    const res = await fetch(`${API_URL}/aumentos/contrato/${contractId}`, {
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, increases: [...] }
  },

  // Crear aumento manual
  create: async (payload) => {
    const res = await fetch(`${API_URL}/aumentos`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify(payload),
    });
    return handleResponse(res); // { success, message, increaseId }
  },

  // 🔥 NUEVO: Calcular aumento automático disponible
  calculateAvailable: async (contractId) => {
    const res = await fetch(`${API_URL}/aumentos/calculate/${contractId}`, {
      headers: getHeaders(true),
    });
    return handleResponse(res);
    // { success, canApply, data: { percentage, newAmount, currentAmount, ... } }
  },

  // 🔥 NUEVO: Aplicar aumento automático
  applyAutomatic: async (contractId, customAmount = null, observations = '') => {
    const res = await fetch(`${API_URL}/aumentos/apply/${contractId}`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify({ customAmount, observations }),
    });
    return handleResponse(res);
    // { success, message, increaseId, details: { previousAmount, newAmount, percentage, effectiveDate } }
  },

  // 🔥 NUEVO: Obtener alertas de aumentos pendientes
  getPendingAlerts: async () => {
    const res = await fetch(`${API_URL}/aumentos/alerts`, {
      headers: getHeaders(true),
    });
    return handleResponse(res);
    // { success, alerts: [...], count }
  },

  // Eliminar setExpiration duplicado (este debe ir en userService)
};

/* ===========================
 *  Subscriptions Service
 *  /api/subscriptions
 * =========================== */
export const subscriptionsService = {
  // Get all available plans
  getPlans: async () => {
    const res = await fetch(`${API_URL}/subscriptions/plans`, {
      headers: getHeaders(true),
    });
    return handleResponse(res);
  },

  // Assign plan to user (Admin only)
  assignPlan: async (userId, planData) => {
    const res = await fetch(`${API_URL}/subscriptions/assign/${userId}`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify(planData),
    });
    return handleResponse(res);
  },

  // Get subscription history for user
  getUserHistory: async (userId) => {
    const res = await fetch(`${API_URL}/subscriptions/history/${userId}`, {
      headers: getHeaders(true),
    });
    return handleResponse(res);
  },

  // Get expiring users dashboard
  getExpiringUsers: async () => {
    const res = await fetch(`${API_URL}/subscriptions/expiring`, {
      headers: getHeaders(true),
    });
    return handleResponse(res);
  },

  // Confirm renewal payment
  confirmRenewal: async (userId, paymentData) => {
    const res = await fetch(`${API_URL}/subscriptions/confirm-renewal/${userId}`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify(paymentData),
    });
    return handleResponse(res);
  },
};

/* ===========================
 *  Boletas Service
 *  /api/boletas
 * =========================== */
export const boletasService = {
  getAll: async () => {
    const res = await fetch(`${API_URL}/boletas`, { headers: getHeaders(true) });
    return handleResponse(res);
  },
  create: async (data) => {
    const res = await fetch(`${API_URL}/boletas`, {
      method: 'POST', headers: getHeaders(true), body: JSON.stringify(data),
    });
    return handleResponse(res);
  },
  update: async (id, data) => {
    const res = await fetch(`${API_URL}/boletas/${id}`, {
      method: 'PUT', headers: getHeaders(true), body: JSON.stringify(data),
    });
    return handleResponse(res);
  },
  delete: async (id) => {
    const res = await fetch(`${API_URL}/boletas/${id}`, {
      method: 'DELETE', headers: getHeaders(true),
    });
    return handleResponse(res);
  },
  uploadFoto: async (id, file) => {
    const formData = new FormData();
    formData.append('foto', file);
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/boletas/${id}/foto`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    return handleResponse(res);
  },
  deleteFoto: async (id) => {
    const res = await fetch(`${API_URL}/boletas/${id}/foto`, {
      method: 'DELETE', headers: getHeaders(true),
    });
    return handleResponse(res);
  },
  getFotoUrl: (fotoUrl) => {
    if (!fotoUrl) return null;
    if (fotoUrl.startsWith('data:')) return fotoUrl;
    if (fotoUrl.startsWith('http')) return fotoUrl;
    const base = (process.env.REACT_APP_API_URL || 'https://rentia-app.onrender.com/api').replace('/api', '');
    return `${base}${fotoUrl}`;
  },
};

/* ===========================
 *  Indices Service
 *  /api/indices
 * =========================== */
export const indicesService = {
  // Obtener histórico de índices del BCRA
  getHistorico: async (months = 24) => {
    const res = await fetch(`${API_URL}/indices/historico?months=${months}`, {
      headers: getHeaders(true),
    });
    return handleResponse(res);
    // { success: true, source: 'bcra'|'mock', indices: [...], lastUpdate }
  },

  // Obtener último valor de un índice
  getLatest: async (type) => {
    const res = await fetch(`${API_URL}/indices/latest/${type}`, {
      headers: getHeaders(true),
    });
    return handleResponse(res);
    // { success: true, index: { type, value, date } }
  },

  // Obtener ICL para una fecha exacta (diario, desde Argly)
  getICLForDate: async (date) => {
    const res = await fetch(`${API_URL}/indices/icl-dia?date=${date}`, {
      headers: getHeaders(true),
    });
    return handleResponse(res);
    // { success: true, fecha: 'YYYY-MM-DD', valor: 30.94, exact: true/false }
  },

  // Guardar índice manualmente
  saveManual: async (date, icl, ipc, observations) => {
    const res = await fetch(`${API_URL}/indices/manual`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify({ date, icl, ipc, observations }),
    });
    return handleResponse(res);
    // { success: true, message }
  },

  // Obtener índices guardados localmente
  getLocal: async () => {
    const res = await fetch(`${API_URL}/indices/local`, {
      headers: getHeaders(true),
    });
    return handleResponse(res);
    // { success: true, indices: [...] }
  },
};

/* ===========================
 *  Logo Service
 *  /api/logo
 * =========================== */
export const logoService = {
  // Upload logo
  uploadLogo: async (logoFile) => {
    const formData = new FormData();
    formData.append('logo', logoFile);

    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/logo/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData,
    });
    return handleResponse(res); // { success, message, logoUrl }
  },

  // Get logo
  getLogo: async () => {
    const res = await fetch(`${API_URL}/logo`, {
      method: 'GET',
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, logoUrl }
  },

  // Delete logo
  deleteLogo: async () => {
    const res = await fetch(`${API_URL}/logo`, {
      method: 'DELETE',
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, message }
  },

  // Get logo URL for display
  getLogoUrl: (logoPath) => {
    if (!logoPath) return null;
    if (logoPath.startsWith('data:')) return logoPath;
    if (logoPath.startsWith('http')) return logoPath;
    return `${API_URL.replace('/api', '')}${logoPath}`;
  },
};

/* ===========================
 *  Messages Service
 *  /api/messages
 * =========================== */
export const messagesService = {
  // Send email
  sendEmail: async (to, subject, message, contractId = null) => {
    const res = await fetch(`${API_URL}/messages/email`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify({ to, subject, message, contractId }),
    });
    return handleResponse(res); // { success, message, messageId }
  },
};

/* ===========================
 *  Owner Reports Service
 *  /api/owner-reports
 * =========================== */
export const ownerReportsService = {
  // Get list of unique owners
  getOwners: async () => {
    const res = await fetch(`${API_URL}/owner-reports/owners`, {
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, owners: [...] }
  },

  // Get monthly report data for an owner
  getMonthlyData: async (ownerDni, year, month) => {
    const res = await fetch(
      `${API_URL}/owner-reports/data?owner_dni=${ownerDni}&year=${year}&month=${month}`,
      {
        headers: getHeaders(true),
      }
    );
    return handleResponse(res); // { success, owner, period, contracts, summary }
  },

  // Generate PDF report
  generatePDF: async (ownerDni, year, month) => {
    const res = await fetch(`${API_URL}/owner-reports/generate-pdf`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify({ owner_dni: ownerDni, year, month }),
    });
    return handleResponse(res); // { success, message, pdfUrl }
  },

  // Download PDF
  downloadPDF: async (pdfUrl) => {
    const token = localStorage.getItem('token');
    const fullUrl = pdfUrl.startsWith('http')
      ? pdfUrl
      : `${API_URL.replace('/api', '')}${pdfUrl}`;

    const res = await fetch(fullUrl, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!res.ok) {
      throw new Error('Error al descargar el PDF');
    }

    const blob = await res.blob();
    return blob;
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// MercadoPago — pagos automáticos de suscripciones
// ─────────────────────────────────────────────────────────────────────────────
export const mpService = {
  // Crea una preferencia de pago en MP y devuelve el link de checkout
  crearPreferencia: async (planCode) => {
    const res = await fetch(`${API_URL}/mp/crear-preferencia`, {
      method: 'POST',
      headers: getHeaders(true),
      body: JSON.stringify({ planCode }),
    });
    return handleResponse(res); // { init_point, sandbox_init_point, preference_id }
  },

  // Obtiene los planes disponibles con precios actualizados
  getPlanes: async () => {
    const res = await fetch(`${API_URL}/mp/planes`, {
      headers: getHeaders(true),
    });
    return handleResponse(res); // { planes: [...] }
  },

  // Refresca los datos del usuario logueado (plan, vencimiento, etc.)
  refreshMe: async () => {
    const res = await fetch(`${API_URL}/auth/me`, {
      headers: getHeaders(true),
    });
    return handleResponse(res); // { success, user: { plan_code, fecha_vencimiento, ... } }
  },
};

export const remindersService = {
  getAll: async () => {
    const res = await fetch(`${API_URL}/reminders`, { headers: getHeaders(true) });
    return handleResponse(res);
  },
  complete: async (id) => {
    const res = await fetch(`${API_URL}/reminders/${id}/complete`, {
      method: 'PUT',
      headers: getHeaders(true),
    });
    return handleResponse(res);
  },
  delete: async (id) => {
    const res = await fetch(`${API_URL}/reminders/${id}`, {
      method: 'DELETE',
      headers: getHeaders(true),
    });
    return handleResponse(res);
  },
};